import { isEnumValue } from "@glideapps/ts-necessities";
import { produce } from "immer";
import numeral from "numeral";

export const quotasCollectionName = "quotas";

export enum QuotaContext {
    App = "app",
}

export enum QuotaKind {
    RowsUsed = "rows-used",
    ExcelRowsUsed = "excel-rows-used",
    AirtableRowsUsed = "airtable-rows-used",
    ZapsAndWebhook = "zaps",
    Reloads = "reloads",
    Signatures = "signatures",
    DeliverEmail = "deliver-email",
    FileBytesUsed = "file-bytes-used",
    BarcodesScanned = "barcodes-scanned",
    MutateTablesAPI = "mutate-tables-api",
    MapPins = "map-pins",
    PrivateUsers = "private-users",
    Geocodes = "geocodes",
}

export const quotaNeedsReset: Record<QuotaKind, boolean> = {
    [QuotaKind.ExcelRowsUsed]: false,
    [QuotaKind.AirtableRowsUsed]: false,
    [QuotaKind.RowsUsed]: false,
    [QuotaKind.ZapsAndWebhook]: true,
    [QuotaKind.Reloads]: true,
    [QuotaKind.Signatures]: true,
    [QuotaKind.DeliverEmail]: true,
    [QuotaKind.FileBytesUsed]: false,
    [QuotaKind.BarcodesScanned]: true,
    [QuotaKind.MutateTablesAPI]: true,
    [QuotaKind.MapPins]: true,
    [QuotaKind.PrivateUsers]: true,
    [QuotaKind.Geocodes]: true,
};

export const allQuotaKinds: QuotaKind[] = Object.values(QuotaKind);

export function isQuotaKind(k: string): k is QuotaKind {
    return Object.keys(quotaNeedsReset).indexOf(k) >= 0;
}

export interface QuotaLimits {
    readonly prepaidUsage: number;
    readonly maxOverage: number;
}

export type QuotaLimitList = Record<QuotaKind, QuotaLimits>;

export const defaultQuotaLimits: QuotaLimitList = {
    [QuotaKind.ExcelRowsUsed]: { prepaidUsage: 10, maxOverage: 0 },
    [QuotaKind.AirtableRowsUsed]: { prepaidUsage: 10, maxOverage: 0 },
    [QuotaKind.RowsUsed]: { prepaidUsage: 500, maxOverage: 0 },
    [QuotaKind.ZapsAndWebhook]: { prepaidUsage: 100, maxOverage: 0 },
    [QuotaKind.Reloads]: { prepaidUsage: 1000, maxOverage: 0 },
    [QuotaKind.Signatures]: { prepaidUsage: 10, maxOverage: 0 },
    [QuotaKind.DeliverEmail]: { prepaidUsage: 0, maxOverage: 0 },
    [QuotaKind.FileBytesUsed]: { prepaidUsage: 100 * 1000 * 1000, maxOverage: 0 },
    [QuotaKind.BarcodesScanned]: { prepaidUsage: 10, maxOverage: 0 },
    [QuotaKind.MapPins]: { prepaidUsage: 10, maxOverage: 0 },
    [QuotaKind.PrivateUsers]: { prepaidUsage: 0, maxOverage: 0 },
    [QuotaKind.Geocodes]: { prepaidUsage: 10, maxOverage: 0 },
    [QuotaKind.MutateTablesAPI]: { prepaidUsage: 50, maxOverage: 0 },
};

export const prepaidUsersOnPrivateAppPlan = 20;

export function applyQuotaHardCaps(quotas: QuotaLimitList): QuotaLimitList {
    return produce(quotas, draft => {
        for (const k in draft) {
            if (!isEnumValue(QuotaKind, k)) continue;
            draft[k].maxOverage = 0;
        }
    });
}

export interface QuotaValues {
    readonly current: number;
    readonly lastResetAt?: Date;
}

interface QuotaInfo {
    readonly kind: QuotaKind;
    readonly shortName: string;
    readonly longName?: string;
    readonly subtitle: string | undefined;
    readonly formatFunc?: (item: number) => string;
}

export const quotaInfos: QuotaInfo[] = [
    {
        kind: QuotaKind.RowsUsed,
        shortName: "Rows",
        subtitle: undefined,
    },
    {
        kind: QuotaKind.FileBytesUsed,
        longName: "Images & Files",
        shortName: "Storage",
        subtitle: undefined,
        formatFunc: i => numeral(i).format("0b"),
    },
    {
        kind: QuotaKind.Reloads,
        shortName: "Edits",
        longName: "Sheet Edits",
        subtitle: "(Per month)",
    },
    {
        kind: QuotaKind.ZapsAndWebhook,
        longName: "Zaps & Webhooks",
        shortName: "Webhooks",
        subtitle: "(Per month)",
    },
    {
        kind: QuotaKind.Signatures,
        shortName: "Signatures",
        subtitle: "(Per month)",
    },
    {
        kind: QuotaKind.DeliverEmail,
        longName: "Emails Sent",
        shortName: "Emails",
        subtitle: "(Per month)",
    },
    {
        kind: QuotaKind.BarcodesScanned,
        longName: "Barcodes Scanned",
        shortName: "Barcodes",
        subtitle: "(Per month)",
    },
    {
        kind: QuotaKind.MutateTablesAPI,
        longName: "Table Mutation API",
        shortName: "API",
        subtitle: "(Per month)",
    },
    {
        kind: QuotaKind.MapPins,
        longName: "Map Pins Created",
        shortName: "Map Pins",
        subtitle: "(Per month)",
    },
    {
        kind: QuotaKind.PrivateUsers,
        longName: "Private User Signins",
        shortName: "Private Users",
        subtitle: "(Per month)",
    },
];

export type AllQuotaValues = Record<QuotaKind, QuotaValues>;

export type QuotaValuesForKinds = Partial<AllQuotaValues>;

export interface QuotaDocument {
    readonly quotas: QuotaValuesForKinds;
    readonly quotaVersions: Partial<Record<QuotaKind, { confirmed: number; next: number }>>;
}

export function makeQuotaDocumentID(context: QuotaContext, id: string): string {
    return `${context}-${id}`;
}
